import { cast, flow, getSnapshot, SnapshotIn, toGenerator, types } from 'mobx-state-tree'

import { ChecklistKind, checklistKindSelectOptions } from '../models/Checklists/ChecklistKind'
import {
  ChecklistEditCloneModel,
  ExistingChecklist,
  ExistingChecklistEditCloneModel,
  ExistingChecklistModel,
  NewChecklistEditCloneModel,
} from '../models/Checklists/ChecklistModel'
import { FetchModel } from '../models/FetchModel'
import { FilterModel } from '../models/common/Filters/FilterModel'
import { FilterType } from '../models/common/Filters/FilterType'
import { SelectFieldFilter, SelectFilterIcon } from '../models/common/Filters/SelectFieldFilter'
import {
  OperationFailure,
  OperationInProgress,
  OperationNotStarted,
  OperationStatusModel,
  OperationSuccess,
} from '../models/common/OperationStatus'

const CHECKLIST_TYPE_FILTER = 'checklistType'
const CHECKLIST_FILTERS: SnapshotIn<typeof FilterModel> = {
  fieldFilters: {
    [CHECKLIST_TYPE_FILTER]: {
      type: FilterType.SELECT,
      icon: SelectFilterIcon.Filter,
      options: checklistKindSelectOptions.slice(), // copy to make non readonly
    },
  },
}

export const ChecklistsStore = FetchModel.named('ChecklistsStore')
  .props({
    filters: types.optional(FilterModel, CHECKLIST_FILTERS),

    checklists: types.maybeNull(types.array(ExistingChecklistModel)),
    selectedChecklist: types.safeReference(ExistingChecklistModel),
    checklistCloneForEditing: types.maybeNull(ChecklistEditCloneModel),

    fetchStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    saveStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    deleteStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
  })
  .views((self) => ({
    get checklistKindFilter(): SelectFieldFilter {
      return self.filters.fieldFilters.get(CHECKLIST_TYPE_FILTER) as SelectFieldFilter
    },
  }))
  .views((self) => ({
    get checklistsUrl() {
      return `${self.baseUrl}checklists/`
    },
    checklistUrl(checklistId: number) {
      return `${this.checklistsUrl}${checklistId}/`
    },
    get isAddingChecklist() {
      return NewChecklistEditCloneModel.is(self.checklistCloneForEditing)
    },
    get filteredChecklists(): Array<ExistingChecklist> | null {
      if (self.checklists === null) return null
      if (self.checklistKindFilter.filterValue === undefined) {
        return self.checklists
      }
      const filterChecklistKind = self.checklistKindFilter.filterValue.value as ChecklistKind
      return self.checklists.filter((checklist) => checklist.checklistKind === filterChecklistKind)
    },
    get openCenterChecklists(): Array<ExistingChecklist> | null {
      if (self.checklists === null) {
        return null
      }
      return self.checklists.filter(checklist => checklist.checklistKind === ChecklistKind.CenterDayOpen)
    },
    get closeCenterChecklists(): Array<ExistingChecklist> | null {
      if (self.checklists === null) {
        return null
      }
      return self.checklists.filter(checklist => checklist.checklistKind === ChecklistKind.CenterDayClose)
    } 
  }))
  .actions((self) => ({
    fetchAndSetChecklists: flow(function* fetchAndSetChecklists() {
      self.fetchStatus = OperationInProgress()

      const loadResult = yield* toGenerator(
        self.loadObjectOrError<SnapshotIn<typeof self.checklists>>(
          self.checklistsUrl,
          {},
          { fetchOperationName: 'Fetch users' },
        ),
      )
      if (loadResult.error) {
        self.fetchStatus = OperationFailure(loadResult.error)
      } else {
        self.checklists = cast(loadResult.result)
        self.checklists?.sort((p1,p2) => (p1.id > p2.id ? 1 : -1))
        self.fetchStatus = OperationSuccess()
      }
    }),
    clearSelectedChecklist(): void {
      self.selectedChecklist = undefined
      self.checklistCloneForEditing = null
    },
    createNewChecklist(): void {
      self.selectedChecklist = undefined
      self.checklistCloneForEditing = NewChecklistEditCloneModel.create()
      self.checklistCloneForEditing.addStep()
    },
    trySetSelectedChecklist(checklistId: number): boolean {
      if (self.checklists === null) {
        console.warn("Can't select checklist when no checklists fetched")
        return false
      }

      const checklist = self.checklists.find((checklist) => checklist.id === checklistId)
      if (checklist === undefined) {
        return false
      }

      self.selectedChecklist = checklist
      self.checklistCloneForEditing = ExistingChecklistEditCloneModel.create(getSnapshot(checklist))

      return true
    },
  }))
  .actions((self) => ({
    saveEditedChecklist: flow(function* saveEditedChecklist() {
      if (self.checklistCloneForEditing === null) {
        throw Error("Can't save edited checklist when no editing clone present")
      }
      self.saveStatus = OperationInProgress()

      const checklistJson = JSON.stringify(getSnapshot(self.checklistCloneForEditing))

      const [url, method] = ExistingChecklistEditCloneModel.is(self.checklistCloneForEditing)
        ? [self.checklistUrl(self.checklistCloneForEditing.id), 'PATCH']
        : [self.checklistsUrl, 'POST']

      const fetchResult = yield* toGenerator(
        self.loadObjectOrError<void>(url, { method, body: checklistJson }, { fetchOperationName: 'Save checklist' }),
      )
      if (fetchResult.error) {
        self.saveStatus = OperationFailure(fetchResult.error)
      } else {
        self.saveStatus = OperationSuccess()
        void self
          .fetchAndSetChecklists() // refresh checklists
          .then(() => {
            // make a fresh editing clone with user from `users`, if needed.
            if (
              self.selectedChecklist !== undefined &&
              ExistingChecklistEditCloneModel.is(self.checklistCloneForEditing) &&
              self.selectedChecklist.id === self.checklistCloneForEditing.id
            ) {
              self.trySetSelectedChecklist(self.selectedChecklist.id)
            }
          })
      }
    }),
    deleteChecklist: flow(function* deleteChecklist(checklistId: number) {
      self.deleteStatus = OperationInProgress()

      const fetchResult = yield* toGenerator(
        self.loadObjectOrError<void>(
          self.checklistUrl(checklistId),
          { method: 'DELETE' },
          { fetchOperationName: 'Delete checklist' },
        ),
      )
      if (fetchResult.error) {
        self.deleteStatus = OperationFailure(fetchResult.error)
      } else {
        if (self.selectedChecklist?.id === checklistId) {
          self.selectedChecklist = undefined
          self.checklistCloneForEditing = null
        }
        self.deleteStatus = OperationSuccess()
        void self.fetchAndSetChecklists() // refresh checklist list
      }
    }),
  }))
